import { useState } from 'react';
import { complianceApi, type EWayBillRecord } from '@/api/compliance.api';
import { parseEWBErrors } from '@/utils/ewbErrorCodes';
import { useToast } from '@/contexts/ToastContext';

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return iso;
  }
}

interface Props {
  ewb: EWayBillRecord;
  onClose: () => void;
  onExtended: () => void;
}

const EXTENSION_REASONS = [
  { code: 1, label: 'Natural Calamity / Severe Weather' },
  { code: 2, label: 'Law and Order Situation' },
  { code: 3, label: 'Transshipment / Vehicle Breakdown' },
  { code: 4, label: 'Accident' },
  { code: 9, label: 'Others' },
];

export default function ExtendSlideOver({ ewb, onClose, onExtended }: Props) {
  const toast = useToast();
  const [vehicleNo, setVehicleNo] = useState(ewb.vehicleNumber || '');
  const [fromPlace, setFromPlace] = useState(ewb.fromAddress?.split(',')[0] || 'FRAZER TOWN');
  const [fromState, setFromState] = useState(ewb.fromGSTIN ? ewb.fromGSTIN.substring(0, 2) : '29');
  const [fromPincode, setFromPincode] = useState('560001');
  const [remainingDistance, setRemainingDistance] = useState(String(ewb.distanceKm || 50));
  const [extnRsnCode, setExtnRsnCode] = useState<number>(1);
  const [extnRemarks, setExtnRemarks] = useState('Natural Calamity');
  const [consignmentStatus, setConsignmentStatus] = useState<'M' | 'T'>('M');
  const [transDocNo, setTransDocNo] = useState('');
  const [transDocDate, setTransDocDate] = useState('');
  const [loading, setLoading] = useState(false);

  const canExtend = Boolean(
    ewb.ewbNumber &&
    fromPlace.trim() &&
    fromState &&
    fromPincode.trim() &&
    remainingDistance &&
    Number(remainingDistance) > 0 &&
    extnRemarks.trim()
  );

  const handleExtend = async () => {
    if (!canExtend) return;
    setLoading(true);

    try {
      const payload = {
        ewbId: ewb.id,
        ewbNo: ewb.ewbNumber,
        vehicleNo: vehicleNo.trim() || undefined,
        fromPlace: fromPlace.trim(),
        fromState: Number(fromState),
        fromPincode: Number(fromPincode),
        remainingDistance: Number(remainingDistance),
        extnRsnCode: Number(extnRsnCode),
        extnRemarks: extnRemarks.trim(),
        consignmentStatus,
        transitType: consignmentStatus === 'M' ? undefined : 'W',
        transDocNo: transDocNo.trim() || undefined,
        transDocDate: transDocDate.trim() || undefined,
        transMode: '1',
      };

      const res = await complianceApi.extendEWayBill(payload);

      if (res?.success) {
        toast.success(res.message || 'E-Way Bill validity extended successfully ✓');
        onExtended();
        onClose();
      } else {
        const parsedErr = parseEWBErrors(res?.errorDetails || res?.message);
        toast.error(parsedErr.toastMessage || res?.message || 'Failed to extend E-Way Bill validity');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to extend E-Way Bill validity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold flex items-center gap-2">
              <i className="ri-history-line text-indigo-400 text-lg" />
              Extend E-Way Bill Validity
            </h2>
            <p className="text-xs text-slate-300 font-mono mt-0.5">EWB No: {ewb.ewbNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-lg cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 space-y-4 text-xs text-slate-700">

          {/* EWB Summary Banner */}
          <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-3.5 space-y-1">
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-500 font-medium">Invoice Number:</span>
              <span className="font-bold text-slate-900">{ewb.invoiceNumber || '—'}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-500 font-medium">Current Valid Until:</span>
              <span className="font-semibold text-emerald-700">{formatDateTime(ewb.validUntil)}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-500 font-medium">Customer / Recipient:</span>
              <span className="font-semibold text-slate-800">{ewb.customerName || '—'}</span>
            </div>
          </div>

          {/* Extension Reason Code */}
          <div>
            <label className="block text-xs font-semibold text-slate-800 mb-1">
              Extension Reason <span className="text-red-500">*</span>
            </label>
            <select
              value={extnRsnCode}
              onChange={(e) => {
                const code = Number(e.target.value);
                setExtnRsnCode(code);
                const found = EXTENSION_REASONS.find(r => r.code === code);
                if (found) setExtnRemarks(found.label);
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
            >
              {EXTENSION_REASONS.map(r => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Extension Remarks */}
          <div>
            <label className="block text-xs font-semibold text-slate-800 mb-1">
              Reason Remarks <span className="text-red-500">*</span>
            </label>
            <textarea
              value={extnRemarks}
              onChange={(e) => setExtnRemarks(e.target.value)}
              rows={2}
              placeholder="e.g. Natural Calamity / Severe Weather delays"
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] resize-none"
            />
          </div>

          {/* Current Location (fromPlace) & State */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-800 mb-1">
                Current Place / City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fromPlace}
                onChange={(e) => setFromPlace(e.target.value)}
                placeholder="e.g. FRAZER TOWN"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-800 mb-1">
                Current State Code <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={fromState}
                onChange={(e) => setFromState(e.target.value)}
                placeholder="e.g. 29"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              />
            </div>
          </div>

          {/* Current Pincode & Remaining Distance */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-800 mb-1">
                Current Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={fromPincode}
                onChange={(e) => setFromPincode(e.target.value)}
                placeholder="e.g. 560001"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-800 mb-1">
                Remaining Distance (KM) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={remainingDistance}
                onChange={(e) => setRemainingDistance(e.target.value)}
                placeholder="e.g. 50"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              />
            </div>
          </div>

          {/* Consignment Status & Vehicle Number */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-800 mb-1">Consignment Status</label>
              <select
                value={consignmentStatus}
                onChange={(e) => setConsignmentStatus(e.target.value as 'M' | 'T')}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              >
                <option value="M">In Movement (M)</option>
                <option value="T">In Transit / Warehouse (T)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-800 mb-1">Vehicle Number</label>
              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                placeholder="e.g. ABC1234"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              />
            </div>
          </div>

          {/* Transport Doc No & Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-800 mb-1">Transporter Doc / LR No</label>
              <input
                type="text"
                value={transDocNo}
                onChange={(e) => setTransDocNo(e.target.value)}
                placeholder="e.g. 12"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-800 mb-1">Transporter Doc Date</label>
              <input
                type="text"
                value={transDocDate}
                onChange={(e) => setTransDocDate(e.target.value)}
                placeholder="e.g. 11/12/2024"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-xs font-semibold text-slate-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleExtend}
              disabled={!canExtend || loading}
              className="flex-1 px-4 py-2.5 text-xs font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-1.5"
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin" /> Extending...</>
              ) : (
                <><i className="ri-check-line" /> Extend E-Way Bill</>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}